import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { grabService, GrabMenuSync } from '../services/grabService';
import { useToast } from '../hooks/useToast';

// shadcn/ui components
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { RefreshCw, Play, CheckCircle2, XCircle, Clock, AlertCircle } from 'lucide-react';

export const ModernMenuSyncDashboard: React.FC = () => {
  const { translate } = useTranslation();
  const { success, error: showError } = useToast();
  const [syncs, setSyncs] = useState<GrabMenuSync[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedSync, setSelectedSync] = useState<GrabMenuSync | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    loadSyncs();
    // Refresh every 30 seconds
    const interval = setInterval(loadSyncs, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadSyncs = async () => {
    try {
      const data = await grabService.getMenuSyncs();
      // Sort by startedAt descending
      data.sort((a, b) => 
        new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
      );
      setSyncs(data);
    } catch (error: any) {
      showError(error.message || 'Failed to load sync history');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncMenu = async () => {
    try {
      setSyncing(true);
      const result = await grabService.syncMenu();
      success(`Menu sync started: ${result.syncId}`);
      setTimeout(() => {
        loadSyncs();
      }, 2000);
    } catch (error: any) {
      showError(error.message || 'Failed to sync menu');
    } finally {
      setSyncing(false);
    }
  };

  const getStatusBadge = (sync: GrabMenuSync) => {
    switch (sync.status) {
      case 'success':
        return (
          <Badge variant="default" className="bg-green-500">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Success
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive">
            <XCircle className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      case 'processing':
        return (
          <Badge variant="secondary">
            <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
            Processing
          </Badge>
        );
      case 'partial':
        return (
          <Badge variant="outline" className="bg-yellow-100">
            <AlertCircle className="w-3 h-3 mr-1" />
            Partial
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
    }
  };

  const getProgress = (sync: GrabMenuSync) => {
    if (sync.itemsTotal === 0) return 0;
    return Math.round((sync.itemsSynced / sync.itemsTotal) * 100);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Menu Sync Dashboard</h2>
          <p className="text-muted-foreground">
            Monitor and manage menu synchronization with Grab
          </p>
        </div>
        <Button onClick={handleSyncMenu} disabled={syncing}>
          {syncing ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Syncing...
            </>
          ) : (
            <>
              <Play className="w-4 h-4 mr-2" />
              Sync Menu Now
            </>
          )}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Syncs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{syncs.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Successful</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">
              {syncs.filter((s) => s.status === 'success').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">
              {syncs.filter((s) => s.status === 'failed').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">In Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">
              {syncs.filter((s) => s.status === 'processing').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sync History */}
      <Card>
        <CardHeader>
          <CardTitle>Sync History</CardTitle>
          <CardDescription>Recent menu synchronization attempts</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : syncs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No sync history found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sync ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Completed</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {syncs.map((sync) => (
                  <TableRow key={sync.id}>
                    <TableCell className="font-mono text-sm">
                      {sync.syncId.substring(0, 8)}...
                    </TableCell>
                    <TableCell>{getStatusBadge(sync)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-500 h-2 rounded-full"
                            style={{ width: `${getProgress(sync)}%` }}
                          />
                        </div>
                        <span className="text-sm">{getProgress(sync)}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {sync.itemsSynced} / {sync.itemsTotal}
                      {sync.itemsFailed > 0 && (
                        <span className="text-red-500 ml-1">
                          ({sync.itemsFailed} failed)
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(sync.startedAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {sync.completedAt
                        ? new Date(sync.completedAt).toLocaleString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedSync(sync);
                          setShowDetails(true);
                        }}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sync Details</DialogTitle>
            <DialogDescription>
              Detailed information about this sync operation
            </DialogDescription>
          </DialogHeader>
          {selectedSync && (
            <div className="space-y-4">
              <div>
                <strong>Sync ID:</strong> {selectedSync.syncId}
              </div>
              <div>
                <strong>Status:</strong> {getStatusBadge(selectedSync)}
              </div>
              <div>
                <strong>Items Synced:</strong> {selectedSync.itemsSynced} /{' '}
                {selectedSync.itemsTotal}
              </div>
              {selectedSync.itemsFailed > 0 && (
                <div>
                  <strong>Items Failed:</strong>{' '}
                  <span className="text-red-500">
                    {selectedSync.itemsFailed}
                  </span>
                </div>
              )}
              <div>
                <strong>Started:</strong>{' '}
                {new Date(selectedSync.startedAt).toLocaleString()}
              </div>
              {selectedSync.completedAt && (
                <div>
                  <strong>Completed:</strong>{' '}
                  {new Date(selectedSync.completedAt).toLocaleString()}
                </div>
              )}
              {selectedSync.errors && selectedSync.errors.length > 0 && (
                <div>
                  <strong>Errors:</strong>
                  <div className="mt-2 space-y-1">
                    {selectedSync.errors.map((error, idx) => (
                      <div
                        key={idx}
                        className="text-sm text-red-500 bg-red-50 p-2 rounded"
                      >
                        <div>
                          <strong>Item:</strong> {error.itemId}
                        </div>
                        {error.productId && (
                          <div>
                            <strong>Product:</strong> {error.productId}
                          </div>
                        )}
                        <div>
                          <strong>Error:</strong> {error.error}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

